import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';

import { Icon } from '@hydra/common/components';
import Display from './Display';
import Buttons from './Buttons';

const useStyles = makeStyles(theme => ({
  loader: {
    marginRight: theme.spacing(1.25),
    padding: theme.spacing(0.5)
  },
  rightSection: {
    height: 35,
    minWidth: '30%',
    textAlign: 'right'
  },
  rightSectionMargin: {
    marginTop: theme.spacing(2)
  },
  hidden: {
    display: 'none !important'
  },
  editorFormRowEditor: {
    minWidth: 0,
    width: '100%'
  },
  editingSection: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    marginLeft: 'auto'
  },
  open: {
    flexDirection: 'column',
    alignItems: 'flex-end'
  }
}));

export const getFieldValue = (formValues, field) => get(formValues, field, null);

export const Editor = props => {
  const classes = useStyles();
  const [saving, setSaving] = useState(false);
  const {
    Component,
    disabled = false,
    editing,
    field,
    fileUpload = false,
    otherFieldOpen,
    preview,
    previewClickable = true,
    placeholder,
    setEditingField,
    saveFormField,
    savePdfFile,
    value,
    editingValue,
    setEditingValue,
    resetEditing,
    maxLength = 0,
    allowNewLines = true,
    displayBlockStyleControls = true,
    defaultContent,
    locked = false
  } = props;

  const handleEdit = () => {
    setEditingValue(value);
    setEditingField(field);
  };

  const handleSave = async () => {
    setSaving(true);

    const saveFunction = fileUpload ? savePdfFile : saveFormField;
    await saveFunction({ field, value: editingValue });

    resetEditing();
    setSaving(false);
  };

  const editorVisible = editing;
  const displayVisible = !editorVisible;
  const isDisabled = saving || disabled || otherFieldOpen || locked;

  const editSectionClassname = classnames(classes.editingSection, editorVisible && classes.open);

  const compProps = {
    value: editing ? editingValue : value,
    onChange: setEditingValue,
    save: handleSave,
    cancel: resetEditing,
    field,
    placeholder: placeholder || '',
    disabled: isDisabled,
    maxLength,
    allowNewLines,
    displayBlockStyleControls,
    defaultContent,
    editorVisible
  };

  return (
    <>
      <div className={editSectionClassname}>
        <div className={editorVisible ? classes.editorFormRowEditor : classes.hidden}>
          <Component {...compProps} />
        </div>

        <Display disabled={isDisabled} previewClickable={previewClickable} visible={displayVisible} onEdit={handleEdit} preview={preview} />

        <div
          className={classnames(
            classes.rightSection,
            editorVisible && classes.rightSectionMargin
          )}
        >
          {saving
            ? <div className={classes.loader}><Icon type='loader' /></div>
            : (
              <Buttons
                disabled={isDisabled}
                editing={editing}
                onEdit={handleEdit}
                onSave={handleSave}
                onCancel={resetEditing}
                hideEdit={locked}
              />
              )}
        </div>
      </div>
    </>
  );
};

Editor.propTypes = {
  Component: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func
  ]),
  editing: PropTypes.bool.isRequired,
  field: PropTypes.string.isRequired,
  otherFieldOpen: PropTypes.bool.isRequired,
  preview: PropTypes.node,
  previewClickable: PropTypes.bool,
  fileUpload: PropTypes.bool,
  saveFormField: PropTypes.func,
  savePdfFile: PropTypes.func,
  setEditingField: PropTypes.func.isRequired,
  setEditingValue: PropTypes.func.isRequired,
  resetEditing: PropTypes.func.isRequired,
  value: PropTypes.any,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  allowNewLines: PropTypes.bool,
  displayBlockStyleControls: PropTypes.bool,
  defaultContent: PropTypes.string
};

export default React.memo(Editor);
